import { useEffect, useMemo, useState } from "react";
import { formatMoney } from "../../../features/utils/formatMoney";
import { useSelector } from "react-redux";
import { applicableFee } from "../../../utils/booking/changeFee";
import { clone } from "../../../features/utils/objClone";
import PassengerBreakdownCard from "./PassengerBreakdownCard";

export const PriceBreakdown = ({ data: defData, callback }) => {
  const [rows, setRows] = useState([]);
  const [lock, setLock] = useState(true);

  const {
    paymentSettings: { fees },
  } = useSelector((state) => state.paymentSettings);

  const travellerMap = useMemo(
    () => ({
      ADT: "ADT",
      Adult: "ADT",
      CHD: "CHD",
      CNN: "CHD",
      Child: "CHD",
      INS: "INF",
      INF: "INF",
      Infant: "INF",
    }),
    []
  );

  const travellerLabel = {
    ADT: "Adult",
    CHD: "Child",
    INF: "Infant",
  };

  // base UI breakdown (per PAX)
  const baseBreakdown = useMemo(
    () => [
      { name: "Change Fee", amount: 0 },
      { name: "Fare Difference", amount: 0 },
      { name: "Penality", amount: 0 },
    ],
    []
  );

  // init rows exactly like your old change-order code (so saved data still loads)
  useEffect(() => {
    const temp = (defData?.passengerInfo || [])?.map((obj, i) => ({
      passengerId: obj?.id || 0,
      type: obj?.travelerType,
      penaltyFee:
        defData?.priceBreakdown?.at(i)?.penaltyFee ||
        rows?.at(i)?.penaltyFee ||
        defData?.penaltyFee ||
        0,
      ticketDifference:
        defData?.priceBreakdown?.at(i)?.ticketDifference ||
        rows?.at(i)?.ticketDifference ||
        defData?.ticketDifference ||
        0,
      changePrice:
        defData?.priceBreakdown?.at(i)?.changePrice ||
        rows?.at(i)?.changePrice ||
        defData?.changePrice ||
        0,
    }));

    if (defData?.priceBreakdown?.length) {
      setRows(
        defData.priceBreakdown.map((item) => {
          const { _id, ...rest } = item;
          const tempPassenger = temp.find(
            (pas) => pas?.passengerId === item?.passengerId
          );
          return { ...rest, type: tempPassenger?.type };
        })
      );
    } else {
      setRows(temp);
    }
    // eslint-disable-next-line
  }, [defData]);

  // keep callback shape SAME: callback(rows)
  useEffect(() => {
    if (lock) setLock(false);
    else callback && callback(rows);
    // eslint-disable-next-line
  }, [rows]);

  // ---------- totals (same math as your old change-order) ----------
  const totalPayable = useMemo(() => {
    return (rows || []).reduce(
      (acc, cur) =>
        acc +
        Number(cur?.changePrice || 0) +
        Number(cur?.penaltyFee || 0) +
        Number(cur?.ticketDifference || 0),
      0
    );
  }, [rows]);

  const fee =
    defData?.changeRequest === "PassengerInfo"
      ? fees?.changePassengerDetailFee
      : fees?.reIssueFee;

  // per passenger service fee (same as before)
  const serviceChargeFee = useMemo(() => {
    return applicableFee(totalPayable, fee);
  }, [totalPayable, fee]);

  const totalFee = useMemo(() => {
    return serviceChargeFee * (rows?.length || 1);
  }, [serviceChargeFee, rows?.length]);

  // ---------- UI grouping like refund ----------
  const travellersByTypeCount = useMemo(() => {
    const map = {};
    for (const r of rows || []) {
      const normalized = travellerMap[r?.type] || r?.type || "ADT";
      map[normalized] = (map[normalized] || 0) + 1;
    }
    return map;
  }, [rows, travellerMap]);

  // represent each type with ONE card, and apply saves to all passengers of that type
  const uniqueTypes = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const r of rows || []) {
      const normalized = travellerMap[r?.type] || r?.type || "ADT";
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      out.push({ normalized, travelerType: normalized });
    }
    return out;
  }, [rows, travellerMap]);

  // build card item.breakDown from ONE passenger of that type
  const getItemForType = (normalizedType) => {
    const anyRow = (rows || []).find(
      (r) => (travellerMap[r?.type] || r?.type || "ADT") === normalizedType
    );

    if (!anyRow) {
      return {
        travelerType: normalizedType,
        breakDown: clone(baseBreakdown),
      };
    }

    return {
      travelerType: normalizedType,
      breakDown: [
        { name: "Change Fee", amount: Number(anyRow?.changePrice || 0) },
        {
          name: "Fare Difference",
          amount: Number(anyRow?.ticketDifference || 0),
        },
        { name: "Penality", amount: Number(anyRow?.penaltyFee || 0) },
      ],
    };
  };

  // save from a card: write to ALL passengers of that type
  const handleSaveBreakdown = (normalizedType, nextBreakDown) => {
    const bd = nextBreakDown || [];

    const getAmt = (label) => {
      const found = bd.find((x) => x?.name === label);
      return Number(found?.amount || 0);
    };

    const nextChange = Math.max(0, getAmt("Change Fee"));
    const nextDiff = Math.max(0, getAmt("Fare Difference"));
    const nextPen = Math.max(0, getAmt("Penality"));

    setRows((prev) => {
      const next = [...(prev || [])];

      for (let i = 0; i < next.length; i++) {
        const nType = travellerMap[next[i]?.type] || next[i]?.type || "ADT";
        if (nType !== normalizedType) continue;

        next[i] = {
          ...next[i],
          changePrice: nextChange,
          ticketDifference: nextDiff,
          penaltyFee: nextPen,
        };
      }

      return next;
    });
  };

  return (
    <div className="w-full bg-[#F6F7FB] flex flex-col p-4 mt-4 rounded-2xl border border-gray-200">
      {/* <div className="mt-2 flex justify-end text-primary1 text-end mb-2 text-sm lg:text-base">
        Price breakdown
      </div> */}
      {defData?.supplierChangePrice ? (
        <div className="flex items-center justify-between rounded-xl bg-white border border-gray-200 px-4 py-3">
          <div className="text-sm text-gray-600">
            Original Supplier Change Price
          </div>
          <div className=" text-red-900">
            {formatMoney(defData?.supplierChangePrice)}
          </div>
        </div>
      ) : (
        <></>
      )}
      <div className="mt-2 space-y-4 pb-4">
        {uniqueTypes.map(({ normalized }) => {
          const passengerCount = travellersByTypeCount[normalized] || 1;
          const item = getItemForType(normalized);

          return (
            <PassengerBreakdownCard
              key={normalized}
              normalized={normalized}
              serviceFee={serviceChargeFee} // per PAX
              passengerCount={passengerCount}
              item={item}
              travelerType={normalized}
              travellerLabel={travellerLabel}
              handleSaveBreakdown={handleSaveBreakdown}
            />
          );
        })}
      </div>

      {/* <div className="w-full border-b border-gray-200 my-4" /> */}

      {/* <div className="flex items-center gap-2 font-bold justify-between text-sm lg:text-base">
        <span className="">Total</span>
        <div className="flex font-bold w-full justify-end text-end text-primary1 text-sm lg:text-base mt-2">
          -{formatMoney(totalFee + totalPayable)}
        </div>
      </div> */}
    </div>
  );
};
